import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/Skeleton";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/Table";

interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => React.ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  keyExtractor: (item: T) => string;
  className?: string;
  mobileRender?: (item: T) => React.ReactNode;
  mobileCardClassName?: string;
  emptyState?: React.ReactNode;
}

export function DataTable<T>({
  columns,
  data,
  loading,
  keyExtractor,
  className,
  mobileRender,
  mobileCardClassName,
  emptyState,
}: DataTableProps<T>) {
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className={cn("hidden overflow-hidden rounded-lg border shadow-sm lg:block", className)}>
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.key} className={col.className}>
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell data-testid="data-table-empty" colSpan={columns.length} className="h-24 text-center text-[var(--color-muted-foreground)]">
                  {emptyState || 'No results'}
                </TableCell>
              </TableRow>
            ) : (
              data.map((item) => (
                <TableRow data-testid="data-table-row" key={keyExtractor(item)}>
                  {columns.map((col) => (
                    <TableCell key={col.key} className={col.className}>
                      {col.render ? col.render(item) : (item as any)[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 lg:hidden">
        {data.length === 0 ? (
          <div data-testid="data-table-empty" className="rounded-lg border bg-[var(--color-card)] p-6 text-center text-sm text-[var(--color-muted-foreground)]">
            {emptyState || 'No results'}
          </div>
        ) : (
          data.map((item) => (
            mobileRender ? (
              <div key={keyExtractor(item)} className={cn("rounded-lg border bg-[var(--color-card)] p-4 shadow-sm", mobileCardClassName)}>
                {mobileRender(item)}
              </div>
            ) : (
              <div key={keyExtractor(item)} className={cn("rounded-lg border bg-[var(--color-card)] p-4 shadow-sm", mobileCardClassName)}>
                {columns.map((col) => (
                  <div key={col.key} className="flex justify-between py-1 text-sm">
                    <span className="text-[var(--color-muted-foreground)]">{col.header}</span>
                    <span className="font-medium text-[var(--color-foreground)]">
                      {col.render ? col.render(item) : (item as any)[col.key]}
                    </span>
                  </div>
                ))}
              </div>
            )
          ))
        )}
      </div>
    </>
  );
}
